import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  TextField, 
  Stack, 
  Grid as MuiGrid, 
  Divider, 
  Link,
  InputAdornment,
  Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { formatCurrency, calculateCartItemFees, GST_TAX_ID, PST_TAX_ID } from '../utils/pricing';
import { useCart } from '../context/CartContext';
import { Link as RouterLink } from 'react-router-dom';

interface CartItemProps {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image?: string;
  thumbnailUrl?: string;
  salePrice?: number;
  onRemove?: (id: string) => void;
  onQuantityChange?: (id: string, quantity: number) => void;
}

// Define Grid component with proper React memo to prevent hooks issues
const Grid = React.memo(function Grid(props: any) {
  return <MuiGrid {...props} />;
});

export const CartItem: React.FC<CartItemProps> = ({
  id,
  name,
  price,
  quantity = 1,
  image,
  thumbnailUrl,
  salePrice,
  onRemove,
  onQuantityChange
}) => {
  const activePrice = salePrice || price;
  const { getCartItem } = useCart();
  const cartItem = getCartItem(id);
  
  // Calculate max available quantity based on product's stock
  const maxQuantity = cartItem?.product ? 
    (cartItem.product.unlimited ? 999 : 
     cartItem.product.inStock ? 
     (cartItem.product.quantity || 0) : 0) : 999;
  
  // Calculate totals including fees if we have the cart item
  let itemTotals;
  
  if (cartItem) {
    // Use the pricing utility to calculate all fees
    const tempItem = {
      ...cartItem,
      price: activePrice,
      quantity: quantity
    };
    
    itemTotals = calculateCartItemFees(tempItem);
  } else {
    itemTotals = {
      subtotal: activePrice * quantity,
      gst: 0,
      pst: 0,
      tax: 0,
      totalDeposit: 0,
      totalEcoFee: 0,
      totalTax: 0,
      total: activePrice * quantity
    };
  }
  
  const handleIncrease = () => {
    if (!onQuantityChange) return;
    
    // Check if new quantity exceeds available stock
    const newQuantity = quantity + 1;
    if (newQuantity > maxQuantity) return;
    
    onQuantityChange(id, newQuantity);
  };

  const handleDecrease = () => {
    if (!onQuantityChange || !onRemove) return;
    
    if (quantity > 1) {
      onQuantityChange(id, quantity - 1);
    } else {
      onRemove(id);
    }
  };

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!onQuantityChange || !onRemove) return;
    
    const newQuantity = parseInt(event.target.value, 10);
    
    if (isNaN(newQuantity)) return;
    
    if (newQuantity <= 0) {
      onRemove(id);
    } else if (newQuantity <= maxQuantity) {
      onQuantityChange(id, newQuantity);
    }
  };

  const handleRemove = () => {
    if (onRemove) {
      onRemove(id);
    }
  };

  const stockWarning = quantity >= maxQuantity;

  return (
    <Box sx={{ py: 2 }}>
      {/* Main flex container */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
      
        {/* Left Area: Image & Details */}
        <Box sx={{ display: 'flex', gap: 2, flexGrow: 1, minWidth: 0 /* Crucial for preventing push */ }}>
          {/* Image */}
          <Box sx={{ width: { xs: 60, sm: 70 }, flexShrink: 0 }}>
            <Link component={RouterLink} to={`/product/${id}`}>
              <Box
                component="img"
                src={image || thumbnailUrl || '/placeholder.png'}
                alt={name}
                sx={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: 1,
                  display: 'block' // Helps prevent extra space
                }}
              />
            </Link>
          </Box>
          
          {/* Product Details */}
          <Box sx={{ pt: 0.5 /* Align text slightly better with image top */ }}>
            <Link
              component={RouterLink}
              to={`/product/${id}`}
              underline="hover"
              color="inherit"
              sx={{ display: 'block' /* Prevent underline covering full width */, width: 'fit-content' }}
            >
              <Typography variant="body1" fontWeight={500}>
                {name}
              </Typography>
            </Link>
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Price: {formatCurrency(activePrice)}
            </Typography>
            
            {itemTotals.totalDeposit > 0 && (
              <Typography variant="body2" color="text.secondary">
                +{formatCurrency(itemTotals.totalDeposit / quantity)} deposit
              </Typography>
            )}
            
            {itemTotals.totalEcoFee > 0 && (
              <Typography variant="body2" color="text.secondary">
                +{formatCurrency(itemTotals.totalEcoFee / quantity)} eco fee
              </Typography>
            )}
            
            {stockWarning && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                Max quantity: {maxQuantity}
              </Typography>
            )}
          </Box>
        </Box>
        
        {/* Right Area: Controls, Price, Delete */}
        <Box sx={{ flexShrink: 0 /* Prevent this area from shrinking */ }}>
          <Stack spacing={1} alignItems="flex-end"> {/* Reduced spacing */} 
            {/* Total Price - Moved to top */} 
            <Stack direction="row" alignItems="center" spacing={1}>

            <Typography variant="body1" fontWeight={600} sx={{ minWidth: '60px', textAlign: 'right', mb: 0.5 /* Add some space below price */ }}>
              {formatCurrency(itemTotals.total)}
            </Typography>

            <IconButton 
                onClick={handleRemove} 
                color="error" 
                size="small"
                sx={{ 
                  border: '0px solid',
                  borderColor: 'error.light',
                  color: 'error.main',
                  width: 20,
                  height: 20,
                  opacity: 0.7,
                  '&:hover': {
                    opacity: 1,
                    backgroundColor: 'error.50'
                  },
                  ml: 1 
                }}
              >
                <DeleteIcon fontSize="inherit" />
              </IconButton>

            </Stack>
            
            {/* Quantity Controls & Delete Button on one line */} 
            <Stack direction="row" alignItems="center" spacing={1}>
              {/* Quantity Controls */}
              <IconButton 
                onClick={handleDecrease}
                size="small"
                sx={{ 
                  border: '1px solid', 
                  borderColor: 'divider',
                  width: 20,
                  height: 20
                }}
              >
                <RemoveIcon fontSize="inherit" />
              </IconButton>
              
              <TextField
                value={quantity}
                onChange={handleQuantityChange}
                InputProps={{
                  inputProps: {
                    min: 1,
                    max: maxQuantity,
                  },
                  sx: {
                    input: {
                      textAlign: 'center', // Center the text inside the input
                      padding: '4px 0',
                    }
                  }
                }}
                size="small"
                sx={{
                  width: '30px',
                  '& .MuiOutlinedInput-root': {
                    height: 30,
                  }
                }}
                error={stockWarning}
              />
              <IconButton 
                onClick={handleIncrease}
                size="small"
                disabled={quantity >= maxQuantity}
                sx={{ 
                  border: '1px solid', 
                  borderColor: 'divider',
                  width: 20,
                  height: 20
                }}
              >
                <AddIcon fontSize="inherit" />
              </IconButton>
            </Stack>
            
            {/* Removed Price & Delete Stack from here */}
            
          </Stack>
        </Box>
        
      </Box>
      <Divider sx={{ mt: 2, opacity: 0.3 }} />
    </Box>
  );
};

export default CartItem; 